import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@web/components/Navbar";
import { Chatbox } from "@web/route-services/chat/Chatbox";
import { SettingsSidebarProvider } from "@web/route-services/chat/settings/SettingsSidebarProvider";
import { Sidepane } from "@web/route-services/chat/Sidepane";
import { useChatPreviewQuery } from "@web/route-services/chat/use-chat-preview-query";
import { useAppContext } from "@web/route-services/root/AppContext";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo } from "react";
import { z } from "zod";
const searchSchema = z.object({ chatID: z.number().optional() });

export const Route = createFileRoute("/chat")({
  component: ChatPage,
  validateSearch: searchSchema
});

function ChatPage() {
  const { chatID } = Route.useSearch();
  const { data: chatPreviewPages, isSuccess } = useChatPreviewQuery();
  const chatPreviews = useMemo(() => chatPreviewPages?.pages.flatMap((page) => page) || [], [chatPreviewPages?.pages]);
  const { isDesktopScreen } = useAppContext();

  /**
   * Disable the bounce scrolling effect on mobile.
   */
  useEffect(() => {
    document.body.style.overscrollBehavior = "none";
    return () => {
      document.body.style.overscrollBehavior = "auto";
    };
  }, []);

  if (isDesktopScreen) {
    return (
      <SettingsSidebarProvider chatID={chatID}>
        <div className="bg-background text-tx-primary flex h-screen w-screen antialiased">
          <Navbar />

          {isSuccess && chatPreviews.length === 0 ? (
            <div className="from-background-gradient via-background to-background flex h-screen w-screen items-center justify-center overflow-auto bg-gradient-to-b antialiased">
              <AnimatePresence mode="wait">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex h-full w-full items-center justify-center pb-10"
                >
                  <p className="text-tx-tertiary select-none text-center text-xl leading-9">
                    You don&apos;t have any chats. <br />
                    Start a chat by going to{" "}
                    <Link to="/" className="text-tx-secondary hover:underline">
                      Explore
                    </Link>{" "}
                    and clicking on a card. <br />
                    (づ ◕‿◕ )づ
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          ) : (
            <div className="from-background-gradient via-background to-background flex h-screen w-screen items-center overflow-auto bg-gradient-to-b antialiased">
              <Sidepane chatID={chatID} />
              {chatID ? (
                <Chatbox key={chatID} chatID={chatID} />
              ) : (
                <div className="flex-1 justify-center">
                  <p className="text-tx-tertiary select-none text-center text-xl leading-9">
                    Resume a <span className="text-tx-secondary">Chat</span> by selecting it from the sidepanel. <br />
                    (づ ◕‿◕ )づ
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </SettingsSidebarProvider>
    );
  }

  return (
    <SettingsSidebarProvider chatID={chatID}>
      {chatID && <Chatbox key={chatID} chatID={chatID} />}
    </SettingsSidebarProvider>
  );
}
